import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Input, Spinner } from '@fluentui/react-components';
import { AppDispatch } from '../../../store';
import { loginThunk } from '../../../store/auth/authThunks';
import {
  selectIsAuthenticated,
  selectAuthError,
  selectAuthLoading,
} from '../../../store/auth/authSelectors';
import { clearError } from '../../../store/auth/authSlice';
import './LoginPage.scss';
import automatedworkflow from "../../../assets/Images/automateworkflo.jpg";
import hospitalityImage from '../../../assets/Images/cilpron9.jpg';

type LoginStep = 'welcome' | 'credentials';

const SLIDES = [
  { image: automatedworkflow,   tagline: ['Solutions for', 'success and', 'driving growth'] },
  { image: hospitalityImage,    tagline: ['Luxury hospitality', 'managed with', 'precision'] },
  { image: '/Themes/Images/ai.jpg',           tagline: ['Intelligent automation', 'powering your', 'business'] },
  { image: '/Themes/Images/productivity.jpg', tagline: ['Productivity tools', 'built for', 'modern teams'] },
  { image: '/Themes/Images/email.jpg',        tagline: ['Enterprise email', 'delivered at', 'scale'] },
  { image: '/Themes/Images/finance.jpg',      tagline: ['Banking and finance', 'reimagined for', 'the digital age'] },
  { image: '/Themes/Images/insurance.jpg',    tagline: ['HMO and insurance', 'protecting what', 'matters most'] },
  { image: '/Themes/Images/crm.jpg',          tagline: ['CRM and accounting', 'every relationship', 'accounted for'] },
  { image: '/Themes/Images/channels.jpg',     tagline: ['Chat SMS and', 'channels that', 'keep you connected'] },
];

const SLIDE_DURATION = 6000;
const FADE_DURATION  = 1200;

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading         = useSelector(selectAuthLoading);
  const authError       = useSelector(selectAuthError);

  const [step, setStep]               = useState<LoginStep>('welcome');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [greeting, setGreeting]       = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [nextSlide, setNextSlide]     = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [taglineVisible, setTaglineVisible] = useState(true);

  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slideTimer      = useRef<ReturnType<typeof setInterval> | null>(null);

  // Greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12)      setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else                setGreeting('Good evening');
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => { dispatch(clearError()); };
  }, [dispatch]);

  // Slideshow
  useEffect(() => {
    slideTimer.current = setInterval(() => {
      const next = (currentSlide + 1) % SLIDES.length;
      setTaglineVisible(false);
      setTimeout(() => {
        setNextSlide(next);
        setTransitioning(true);
        setTimeout(() => {
          setCurrentSlide(next);
          setNextSlide(null);
          setTransitioning(false);
          setTaglineVisible(true);
        }, FADE_DURATION);
      }, 400);
    }, SLIDE_DURATION);
    return () => { if (slideTimer.current) clearInterval(slideTimer.current); };
  }, [currentSlide]);

  // Inactivity reset
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setStep('welcome');
      setEmail('');
      setPassword('');
      dispatch(clearError());
    }, 60000);
  }, [dispatch]);

  useEffect(() => {
    if (step === 'credentials') {
      window.addEventListener('mousemove', resetInactivityTimer);
      window.addEventListener('keydown', resetInactivityTimer);
      resetInactivityTimer();
    }
    return () => {
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keydown', resetInactivityTimer);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [step, resetInactivityTimer]);

  // ✅ handleSubmit defined with useCallback so the Enter key effect
  //    always has the latest email/password values — no stale closure
  const handleSubmit = useCallback(async () => {
    if (!email.trim() || !password.trim()) return;
    const result = await dispatch(loginThunk({ email: email.trim(), password }));
    if (loginThunk.fulfilled.match(result)) {
      navigate('/dashboard', { replace: true });
    }
  }, [dispatch, email, password, navigate]);

  // ✅ Enter key handler depends on handleSubmit — now always fresh
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (step === 'welcome') {
          setStep('credentials');
        } else if (step === 'credentials') {
          handleSubmit();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, handleSubmit]); // ✅ handleSubmit in deps — no stale closure

  const slide = SLIDES[currentSlide];

  return (
    <div className="lp-root">
      <div
        className="lp-bg lp-bg--current"
        style={{ backgroundImage: `url('${slide.image}')` }}
      />
      {nextSlide !== null && (
        <div
          className={`lp-bg lp-bg--next${transitioning ? ' lp-bg--next-visible' : ''}`}
          style={{ backgroundImage: `url('${SLIDES[nextSlide].image}')` }}
        />
      )}
      <div className="lp-overlay" />

      <div className="lp-indicators" aria-hidden="true">
        {SLIDES.map((_, i) => (
          <span
            key={i}
            className={`lp-indicator${i === currentSlide ? ' lp-indicator--active' : ''}`}
          />
        ))}
      </div>

      <div className="lp-shell">
        {step === 'welcome' && (
          <div className="lp-welcome">
            <div className="lp-welcome__bound">
              <p className="lp-welcome__greeting">{greeting}</p>

              <h1 className={`lp-welcome__tagline${taglineVisible ? ' lp-welcome__tagline--visible' : ''}`}>
                {slide.tagline.map((line, i) => (
                  <span
                    key={`${currentSlide}-${i}`}
                    className="lp-welcome__tagline-line"
                    style={{ animationDelay: taglineVisible ? `${i * 120}ms` : '0ms' }}
                  >
                    {line}
                  </span>
                ))}
              </h1>

              <p className="lp-welcome__sub">
                <a href="/" className="lp-welcome__brand">Cilpron</a>
                {' '}— designing the future for true visionaries
              </p>

              <button
                className="lp-welcome__cta"
                onClick={() => setStep('credentials')}
              >
                Sign in to your workspace
              </button>
            </div>
          </div>
        )}

        {step === 'credentials' && (
          <div className="lp-panel">
            {loading ? (
              <div className="lp-panel__spinner">
                <Spinner size="huge" />
              </div>
            ) : (
              <>
                <div className="lp-panel__avatar" aria-hidden="true">
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="22" fill="rgba(255,255,255,0.1)" />
                    <circle cx="22" cy="18" r="8"  fill="rgba(255,255,255,0.55)" />
                    <ellipse cx="22" cy="36" rx="13" ry="8" fill="rgba(255,255,255,0.35)" />
                  </svg>
                </div>

                <h2 className="lp-panel__title">Welcome back</h2>
                <p className="lp-panel__sub">Sign in to your Cilpron workspace</p>

                {authError && (
                  <div className="lp-panel__error" role="alert">
                    <span className="lp-panel__error-icon">⚠</span>
                    {authError}
                  </div>
                )}

                <div className="lp-panel__fields">
                  <Input
                    className="lp-panel__input"
                    placeholder="Email address"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (authError) dispatch(clearError());
                    }}
                    appearance="filled-darker"
                    autoComplete="email"
                    autoFocus
                  />
                  <Input
                    className="lp-panel__input"
                    placeholder="Password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (authError) dispatch(clearError());
                    }}
                    appearance="filled-darker"
                    autoComplete="current-password"
                  />
                </div>

                <button
                  className={`lp-panel__submit${(!email.trim() || !password.trim()) ? ' lp-panel__submit--disabled' : ''}`}
                  onClick={handleSubmit}
                  disabled={!email.trim() || !password.trim()}
                >
                  Sign in
                </button>

                <button
                  className="lp-panel__back"
                  onClick={() => {
                    setStep('welcome');
                    setEmail('');
                    setPassword('');
                    dispatch(clearError());
                  }}
                >
                  ← Back
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;